import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FileSpreadsheet,
  Check,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  ArrowLeftRight,
} from "lucide-react";
import PageWrapper from "components/PageWrapper";
import { getColumnMappingAPI, bomColumnKeySaveAPI } from "actions/bomActions";
import LoadingComponent from "components/LoadingComponent";

const BomColumnMappingPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bomData, setBomData] = useState({
    loading: true,
    rows: [],
    tableColumns: [],
    error: "",
  });
  const [openSelect, setOpenSelect] = useState(null); // "columnRow" | "partNumberKey" | "qtyKey" | null

  const handleChange = (name, value) => {
    let updatedBomData = {
      ...bomData,
      [name]: value,
      columnsUpdated: !value ? false : true,
    };

    if (name === "columnRow" && value) {
      updatedBomData = {
        ...updatedBomData,
        columnsUpdated: true,
        columns: updatedBomData.rows[value].name.split(",").map((item) => {
          return {
            name: item,
            value: item,
          };
        }),
      };
    }

    setBomData(updatedBomData);
  };

  const onLoad = async () => {
    setBomData((prev) => ({ ...prev, loading: true, error: "" }));

    try {
      const response = await getColumnMappingAPI(id);
      if (response.status) {
        const formattedRows = response.data?.bomData?.map((row, i) => ({
          i,
          row,
          string: row.toString(),
          name: row.toString(),
          value: i,
        }));

        setBomData((prev) => ({
          ...prev,
          loading: false,
          data: response.data,
          rows: formattedRows,
          columns: response.data?.columns?.map((column) => ({
            name: column,
            value: column,
          })),
        }));
      } else {
        setBomData((prev) => ({
          ...prev,
          loading: false,
          error: response.message || "Failed to load BOM column mapping.",
        }));
      }
    } catch (error) {
      setBomData((prev) => ({
        ...prev,
        loading: false,
        error: "Something went wrong while loading BOM column mapping.",
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      setBomData((prev) => ({ ...prev, loading: true, error: "" }));

      const payload = {
        uniqueId: id,
        partNumberKey: bomData.partNumberKey,
        qtyKey: bomData.qtyKey,
        rowIndex: bomData.columnRow,
      };
      const response = await bomColumnKeySaveAPI(payload);

      if (!response.status) {
        setBomData((prev) => ({
          ...prev,
          loading: false,
          error: response.message || "Failed to save column mapping.",
        }));
      } else {
        navigate(`/bom-tool/${id}`);
      }
    } catch (error) {
      setBomData((prev) => ({
        ...prev,
        loading: false,
        error: "Something went wrong, please try again later.",
      }));
    }
  };

  useEffect(() => {
    if (id) onLoad();
  }, [id]);

  // Close any open dropdown on outside click.
  useEffect(() => {
    if (!openSelect) return;
    const close = () => setOpenSelect(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [openSelect]);

  const allRows = bomData.rows || [];
  const columns = bomData.columns || [];
  const headerIdx =
    bomData.columnRow !== undefined &&
    bomData.columnRow !== "" &&
    bomData.columnRow !== null
      ? parseInt(bomData.columnRow)
      : null;
  const ready = headerIdx != null && bomData.partNumberKey && bomData.qtyKey;
  const dataRowCount = headerIdx != null ? allRows.length - headerIdx - 1 : 0;
  const pnColIdx = columns.findIndex((c) => String(c.value) === String(bomData.partNumberKey));
  const qtyColIdx = columns.findIndex((c) => String(c.value) === String(bomData.qtyKey));
  const previewColCount = headerIdx != null ? columns.length : allRows[0]?.row?.length || 0;
  const fileName = bomData?.data?.filename || bomData?.data?.file_name || "BOM file";

  const colValueCount = (idx) => {
    if (headerIdx == null) return 0;
    let n = 0;
    for (let r = 0; r < allRows.length; r++) {
      if (allRows[r].i <= headerIdx) continue;
      const cell = allRows[r].row[idx];
      if (cell != null && String(cell).trim() !== "") n++;
    }
    return n;
  };

  const toggleSelect = (e, key, enabled = true) => {
    e.stopPropagation();
    if (!enabled) return;
    setOpenSelect(openSelect === key ? null : key);
  };

  const pickOption = (e, name, value) => {
    e.stopPropagation();
    handleChange(name, value);
    setOpenSelect(null);
  };

  const selectBase =
    "flex items-center gap-2 h-9 px-3 bg-white border border-s360-stroke1 border-b-2 rounded-md text-[13px] text-s360-fg1 cursor-pointer transition-colors";

  return (
    <PageWrapper>
      <div className="flex flex-col gap-[18px]">
        {/* Page head */}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-[28px] leading-[34px] font-bold tracking-[-0.015em] text-s360-ink">
            Map BOM columns
          </h1>
          <span className="text-sm text-s360-fg3">
            Tell SmartBuy which columns hold the part numbers and quantities.
          </span>
        </div>

        {bomData.loading && <LoadingComponent message="Loading..." />}
        {bomData.error && (
          <div className="text-s360-danger bg-s360-danger-bg border border-[#F1BBC0] rounded-md px-3 py-2.5 text-sm font-semibold">
            {bomData.error}
          </div>
        )}

        {!bomData.loading && allRows.length > 0 && (
          <>
            {/* Stepper */}
            <div className="grid grid-cols-1 md:grid-cols-3 bg-white rounded-md shadow-sm overflow-hidden">
              <div className="flex items-center gap-3 px-[22px] py-3.5">
                <span className="w-7 h-7 rounded-full bg-s360-success text-white inline-flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-s360-fg2">Upload file</div>
                  <div className="text-[11px] text-s360-fg3 mt-0.5 truncate max-w-[180px]">{fileName}</div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-[22px] py-3.5 md:border-l border-s360-stroke1">
                <span className="w-7 h-7 rounded-full bg-s360-violet text-white inline-flex items-center justify-center shrink-0 ring-[3px] ring-s360-violet-50">
                  2
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-s360-violet-900">Map columns</div>
                  <div className="text-[11px] text-s360-fg3 mt-0.5">
                    {ready ? "Ready to analyze" : "Confirm mapping"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 px-[22px] py-3.5 md:border-l border-s360-stroke1">
                <span className="w-7 h-7 rounded-full bg-s360-bg3 text-s360-fg2 inline-flex items-center justify-center shrink-0 font-bold text-[13px]">
                  3
                </span>
                <div>
                  <div className="text-[13px] font-semibold text-s360-fg2">Analyze across distributors</div>
                  <div className="text-[11px] text-s360-fg3 mt-0.5">Benchmark pricing &amp; stock</div>
                </div>
              </div>
            </div>

            {/* File strip */}
            <div className="bg-white rounded-md shadow-sm px-[18px] py-3.5 grid grid-cols-[auto_1fr_auto] gap-4 items-center">
              <span className="w-10 h-10 rounded-lg bg-s360-violet-50 text-s360-violet inline-flex items-center justify-center shrink-0">
                <FileSpreadsheet className="w-5 h-5" />
              </span>
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="font-mono text-[15px] font-semibold text-s360-ink truncate">{fileName}</span>
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-xs text-s360-fg2 bg-s360-bg2 border border-s360-stroke1 rounded-full px-2.5 py-px">
                    <strong className="text-s360-fg1 font-semibold">{previewColCount}</strong> columns
                  </span>
                  <span className="text-xs text-s360-fg2 bg-s360-bg2 border border-s360-stroke1 rounded-full px-2.5 py-px">
                    <strong className="text-s360-fg1 font-semibold">{allRows.length}</strong> rows
                  </span>
                  {headerIdx != null && (
                    <span className="text-xs text-s360-fg2 bg-s360-bg2 border border-s360-stroke1 rounded-full px-2.5 py-px">
                      <strong className="text-s360-fg1 font-semibold">{dataRowCount}</strong> data rows
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2 whitespace-nowrap"
              >
                <ArrowLeftRight className="w-4 h-4" /> Replace file
              </button>
            </div>

            {/* Mapping card */}
            <div className="bg-white rounded-md shadow-sm flex flex-col">
              <div className="px-[18px] py-3.5 border-b border-s360-stroke1">
                <h3 className="text-base font-semibold text-s360-fg1 m-0">Column mapping</h3>
                <div className="text-xs text-s360-fg3 mt-0.5">
                  Pick the row containing your column names, then select where the part numbers and
                  quantities live.
                </div>
              </div>

              <div className="p-[18px] grid grid-cols-1 md:grid-cols-3 gap-[18px] items-start">
                {/* Header row */}
                <div className="flex flex-col gap-1.5 relative">
                  <label className="text-xs font-semibold text-s360-fg2 flex items-center gap-1.5">
                    Header row <span className="text-s360-danger font-bold">*</span>
                  </label>
                  <div
                    className={`${selectBase} ${
                      openSelect === "columnRow" ? "border-s360-violet border-b-s360-violet" : ""
                    } ${headerIdx == null ? "border-s360-danger border-b-s360-danger" : ""}`}
                    onClick={(e) => toggleSelect(e, "columnRow")}
                  >
                    <span
                      className={`flex-1 min-w-0 truncate ${headerIdx == null ? "text-s360-fg-disabled" : ""}`}
                    >
                      {headerIdx != null
                        ? `Row ${headerIdx + 1} — ${allRows[headerIdx]?.row
                            ?.filter(Boolean)
                            .slice(0, 3)
                            .join(", ")}`
                        : "Pick the row containing your column names"}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-s360-fg3 shrink-0" />
                  </div>
                  {openSelect === "columnRow" && (
                    <div className="absolute top-full mt-1.5 left-0 right-0 max-h-[220px] overflow-y-auto border border-s360-stroke1 rounded-md bg-white shadow-lg z-20">
                      {allRows.map((row) => (
                        <div
                          key={row.value}
                          className={`px-3 py-2 text-[13px] cursor-pointer ${
                            String(headerIdx) === String(row.value)
                              ? "bg-s360-violet text-white"
                              : "text-s360-fg1 hover:bg-s360-violet-50"
                          }`}
                          onClick={(e) => pickOption(e, "columnRow", String(row.value))}
                        >
                          Row {row.i + 1}:{" "}
                          {row.row.filter(Boolean).slice(0, 4).join(", ") || "(empty)"}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Part number column */}
                <div className="flex flex-col gap-1.5 relative">
                  <label className="text-xs font-semibold text-s360-fg2 flex items-center gap-1.5">
                    Part number column <span className="text-s360-danger font-bold">*</span>
                  </label>
                  <div
                    className={`${selectBase} ${
                      openSelect === "partNumberKey" ? "border-s360-violet border-b-s360-violet" : ""
                    } ${!bomData.partNumberKey ? "border-s360-danger border-b-s360-danger" : ""} ${
                      headerIdx == null ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={(e) => toggleSelect(e, "partNumberKey", headerIdx != null)}
                  >
                    <span
                      className={`flex-1 min-w-0 truncate ${!bomData.partNumberKey ? "text-s360-fg-disabled" : ""}`}
                    >
                      {bomData.partNumberKey || "Select column…"}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-s360-fg3 shrink-0" />
                  </div>
                  {openSelect === "partNumberKey" && headerIdx != null && (
                    <div className="absolute top-full mt-1.5 left-0 right-0 max-h-[220px] overflow-y-auto border border-s360-stroke1 rounded-md bg-white shadow-lg z-20">
                      {columns.map((col, i) => (
                        <div
                          key={i}
                          className={`px-3 py-2 text-[13px] cursor-pointer flex items-center gap-2 ${
                            bomData.partNumberKey === col.value
                              ? "bg-s360-violet text-white"
                              : "text-s360-fg1 hover:bg-s360-violet-50"
                          }`}
                          onClick={(e) => pickOption(e, "partNumberKey", col.value)}
                        >
                          {col.name}
                          <span
                            className={`ml-auto font-mono text-[11px] ${
                              bomData.partNumberKey === col.value ? "text-white/70" : "text-s360-fg3"
                            }`}
                          >
                            {colValueCount(i)} values
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="text-[11px] text-s360-fg3">
                    SmartBuy will look up each MPN across distributors.
                  </span>
                </div>

                {/* Quantity column */}
                <div className="flex flex-col gap-1.5 relative">
                  <label className="text-xs font-semibold text-s360-fg2 flex items-center gap-1.5">
                    Quantity column <span className="text-s360-danger font-bold">*</span>
                  </label>
                  <div
                    className={`${selectBase} ${
                      openSelect === "qtyKey" ? "border-s360-violet border-b-s360-violet" : ""
                    } ${!bomData.qtyKey ? "border-s360-danger border-b-s360-danger" : ""} ${
                      headerIdx == null ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                    onClick={(e) => toggleSelect(e, "qtyKey", headerIdx != null)}
                  >
                    <span className={`flex-1 min-w-0 truncate ${!bomData.qtyKey ? "text-s360-fg-disabled" : ""}`}>
                      {bomData.qtyKey || "Select column…"}
                    </span>
                    <ChevronDown className="w-3.5 h-3.5 text-s360-fg3 shrink-0" />
                  </div>
                  {openSelect === "qtyKey" && headerIdx != null && (
                    <div className="absolute top-full mt-1.5 left-0 right-0 max-h-[220px] overflow-y-auto border border-s360-stroke1 rounded-md bg-white shadow-lg z-20">
                      {columns.map((col, i) => (
                        <div
                          key={i}
                          className={`px-3 py-2 text-[13px] cursor-pointer flex items-center gap-2 ${
                            bomData.qtyKey === col.value
                              ? "bg-s360-violet text-white"
                              : "text-s360-fg1 hover:bg-s360-violet-50"
                          }`}
                          onClick={(e) => pickOption(e, "qtyKey", col.value)}
                        >
                          {col.name}
                          <span
                            className={`ml-auto font-mono text-[11px] ${
                              bomData.qtyKey === col.value ? "text-white/70" : "text-s360-fg3"
                            }`}
                          >
                            {colValueCount(i)} values
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="text-[11px] text-s360-fg3">Used as the RFQ quantity per part.</span>
                </div>
              </div>

              {/* Validation summary */}
              <div className="px-[18px] py-3 border-t border-s360-stroke1 bg-s360-bg2 flex flex-wrap gap-y-2 gap-x-6">
                <div
                  className={`flex items-center gap-2 text-xs ${
                    headerIdx != null ? "text-s360-success" : "text-s360-fg3"
                  }`}
                >
                  {headerIdx != null ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <Circle className="w-3.5 h-3.5" />
                  )}
                  {headerIdx != null ? `Header row · row ${headerIdx + 1}` : "Pick the header row"}
                </div>
                <div
                  className={`flex items-center gap-2 text-xs ${
                    bomData.partNumberKey ? "text-s360-success" : "text-s360-fg3"
                  }`}
                >
                  {bomData.partNumberKey ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <Circle className="w-3.5 h-3.5" />
                  )}
                  {bomData.partNumberKey
                    ? `Part number column · ${bomData.partNumberKey}`
                    : "Pick the part number column"}
                </div>
                <div
                  className={`flex items-center gap-2 text-xs ${
                    bomData.qtyKey ? "text-s360-success" : "text-s360-fg3"
                  }`}
                >
                  {bomData.qtyKey ? (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                  ) : (
                    <Circle className="w-3.5 h-3.5" />
                  )}
                  {bomData.qtyKey ? `Quantity column · ${bomData.qtyKey}` : "Pick the quantity column"}
                </div>
              </div>

              {/* Submit row */}
              <div className="px-[18px] py-3.5 border-t border-s360-stroke1 flex items-center gap-2.5">
                <button
                  onClick={() => navigate(-1)}
                  className="bg-transparent border-0 text-s360-fg3 text-[13px] font-semibold cursor-pointer hover:text-s360-fg1"
                >
                  Cancel
                </button>
                <span className="flex-1" />
                <button
                  disabled={!ready || bomData.loading}
                  onClick={handleSubmit}
                  className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-s360-violet text-white text-sm font-semibold hover:bg-s360-violet-600 transition-colors ${
                    !ready || bomData.loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
                >
                  Analyze BOM <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Preview pane */}
            <div className="bg-white rounded-md shadow-sm flex flex-col overflow-hidden">
              <div className="px-[18px] py-3.5 border-b border-s360-stroke1 flex items-center gap-3 flex-wrap">
                <h3 className="text-base font-semibold text-s360-fg1 m-0">Preview</h3>
                <span className="text-xs text-s360-fg3">from {fileName}</span>
                <div className="ml-auto flex gap-3 text-[11px] text-s360-fg3">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-[3px] bg-s360-violet-50 border-[1.5px] border-s360-violet" /> header row
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-[3px] bg-s360-success-bg border-[1.5px] border-s360-success" /> part number
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-3 rounded-[3px] bg-s360-teal-50 border-[1.5px] border-s360-teal" /> quantity
                  </span>
                </div>
              </div>
              <div className="overflow-auto max-h-[540px]">
                <table className="w-full border-collapse text-xs">
                  <thead>
                    <tr>
                      <th className="sticky top-0 left-0 z-10 w-9 text-center font-mono text-[11px] text-s360-fg3 bg-s360-bg3 border-b border-r border-s360-stroke1 px-3 py-2.5">
                        #
                      </th>
                      {Array.from({ length: previewColCount }).map((_, i) => {
                        const isPn = headerIdx != null && i === pnColIdx;
                        const isQty = headerIdx != null && i === qtyColIdx;
                        const label =
                          headerIdx != null
                            ? columns[i]?.name
                            : `Col ${String.fromCharCode(65 + i)}`;
                        return (
                          <th
                            key={i}
                            className={`sticky top-0 text-left font-semibold text-[11px] uppercase tracking-[0.06em] border-b border-s360-stroke1 px-3 py-2.5 whitespace-nowrap ${
                              isPn
                                ? "bg-s360-success-bg text-s360-success-ink"
                                : isQty
                                ? "bg-s360-teal-50 text-[#0a5450]"
                                : "bg-s360-bg2 text-s360-fg3"
                            }`}
                          >
                            {isPn && (
                              <span className="block font-bold text-[9px] tracking-[0.14em] mb-0.5 opacity-80">
                                PART NUMBER
                              </span>
                            )}
                            {isQty && (
                              <span className="block font-bold text-[9px] tracking-[0.14em] mb-0.5 opacity-80">
                                QUANTITY
                              </span>
                            )}
                            {label || `Col ${String.fromCharCode(65 + i)}`}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {allRows.map((row) => {
                      const isHeader = headerIdx != null && row.i === headerIdx;
                      const isSkipped = headerIdx != null && row.i < headerIdx;
                      return (
                        <tr
                          key={row.i}
                          className={`${isHeader ? "" : "hover:bg-s360-stripe"} ${
                            isSkipped ? "opacity-40" : ""
                          }`}
                        >
                          <td
                            className={`sticky left-0 z-[1] w-9 text-center font-mono text-[11px] border-r border-b border-s360-bg2 px-3 py-2.5 ${
                              isHeader
                                ? "bg-s360-violet text-white font-bold"
                                : "bg-s360-bg2 text-s360-fg3"
                            }`}
                          >
                            {row.i + 1}
                          </td>
                          {Array.from({ length: previewColCount }).map((_, cIdx) => {
                            const cell = row.row[cIdx];
                            const isPn = headerIdx != null && cIdx === pnColIdx;
                            const isQty = headerIdx != null && cIdx === qtyColIdx;
                            const headerCellCls = isHeader
                              ? "bg-s360-violet-50 text-s360-violet-900 font-bold uppercase tracking-[0.05em]"
                              : isPn
                              ? "bg-[#F2FBF5] font-mono font-semibold text-s360-fg1"
                              : isQty
                              ? "bg-[#ECFEFB] font-mono font-semibold text-s360-fg1"
                              : "text-s360-fg1";
                            return (
                              <td
                                key={cIdx}
                                title={cell}
                                className={`px-3 py-2.5 border-b border-s360-bg2 align-middle whitespace-nowrap max-w-[240px] overflow-hidden text-ellipsis ${headerCellCls}`}
                              >
                                {cell || (cell === 0 ? "0" : "")}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-3.5 py-2.5 border-t border-s360-stroke1 text-[11px] text-s360-fg3">
                {headerIdx != null ? (
                  <>
                    <strong className="text-s360-fg1 font-semibold">{dataRowCount}</strong> data row
                    {dataRowCount !== 1 ? "s" : ""} will be analyzed · rows above the header are skipped
                  </>
                ) : (
                  "Pick a header row to see how SmartBuy reads your file"
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </PageWrapper>
  );
};

export default BomColumnMappingPage;
